import React from 'react';
import { cn } from './utils';

export type GradientVariant = 'primary' | 'action' | 'warning' | 'success' | 'entity' | 'inactive' | 'critical';

interface GradientCardProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
    variant?: GradientVariant;
    isDarkMode?: boolean;
    innerClassName?: string;
}

// Full gradient for dark mode
const darkGradients: Record<GradientVariant, string> = {
    primary: "from-blue-500 via-blue-600 to-cyan-500",
    action: "from-rose-500 via-rose-600 to-pink-500",
    warning: "from-amber-500 via-amber-600 to-orange-500",
    success: "from-emerald-500 via-emerald-600 to-green-500",
    entity: "from-purple-500 via-purple-600 to-indigo-500",
    inactive: "from-slate-500 via-slate-600 to-gray-500",
    critical: "from-orange-500 via-orange-600 to-red-500"
};

// Soft gradient for light mode (border effect)
const lightGradients: Record<GradientVariant, string> = {
    primary: "from-blue-400/20 via-blue-500/20 to-cyan-400/20",
    action: "from-rose-400/20 via-rose-500/20 to-pink-400/20",
    warning: "from-amber-400/20 via-amber-500/20 to-orange-400/20",
    success: "from-emerald-400/20 via-emerald-500/20 to-green-400/20",
    entity: "from-purple-400/20 via-purple-500/20 to-indigo-400/20",
    inactive: "from-slate-400/20 via-slate-500/20 to-gray-400/20",
    critical: "from-orange-400/20 via-orange-500/20 to-red-400/20"
};

export function GradientCard({
    children,
    variant = 'primary',
    isDarkMode = false,
    className,
    innerClassName,
    onClick,
    ...props
}: GradientCardProps) {
    // Check for dark mode from class on document if not passed
    const dark = isDarkMode || (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

    return (
        <div
            onClick={onClick}
            className={cn(
                "relative overflow-hidden rounded-2xl shadow-sm transition-transform hover:scale-[1.01] group",
                dark
                    ? `bg-gradient-to-br ${darkGradients[variant]} p-4`
                    : `bg-gradient-to-br ${lightGradients[variant]} backdrop-blur p-1 border-3 border-white/60`,
                onClick ? "cursor-pointer" : "",
                className
            )}
            {...props}
        >
            <div className={cn(
                dark
                    ? ""
                    : "bg-white/90 backdrop-blur rounded-[0.9rem] p-4 h-full",
                innerClassName
            )}>
                {children}
            </div>
        </div>
    );
}
